/**
 * Cliente das funções `functions/api/producao/**`. Tipos espelhados de
 * `functions/lib/producao/*` (outro tsconfig — não importável daqui).
 */
import type { AppShell } from "@/App"

export type JobStatus = "por_atribuir" | "planeada" | "execucao" | "parada" | "concluida" | "cancelada"
export type JobKind = "solar" | "ac" | "bc_aqs" | "bc_aq"
export type TrelloSyncStatus = "ok" | "pendente" | "erro"
/** Onde vive o estado da obra: tabelas da app (`local`) ou contratos do Koortex (`host`). */
export type ContractsBackend = "local" | "host"

export interface Job {
  id: string
  odoo_sale_order_id: string | number | null
  odoo_sale_order_name: string | null
  proposal_id: string | null
  client_name: string
  address: string | null
  city: string | null
  kind: JobKind | null
  kind_summary: string | null
  value_eur: string | number | null
  status: JobStatus
  stop_reason: string | null
  scheduled_date: string | null
  duration_days: number
  van_id: string | null
  lead_technician_id: string | null
  note: string | null
  trello_card_id: string | null
  trello_card_url: string | null
  trello_sync_status: TrelloSyncStatus | null
  trello_synced_at: string | null
  trello_error: string | null
  version: number
  completed_at: string | null
  /** Contrato do Koortex desta obra (modo `host`). */
  contract_id?: string | null
  /** Modo `host` (quadro): o resumo do contrato — as colunas são o estado dele. */
  contract?: ContractSummary | null
  /** Modo `host` (detalhe): o guia do contrato. */
  contract_guide?: ContractGuideView | null
  /** Modo `host`: o contrato não se leu (o detalhe mostra porquê). */
  contract_error?: string
  /** M6: ao abrir, a obra parada na app foi retomada porque o contrato foi retomado no Koortex. */
  contract_resumed?: boolean
  /** M4: o último fecho do telemóvel está em erro (com ou sem a assinatura do cliente). */
  field_closure_error?: { event_uuid: string; signed: boolean; error: string | null } | null
  /** I1: parada pelo técnico com a regra antiga (antes de 06/10) — só o gestor a retoma. */
  legacy_field_stop?: { event_uuid: string; technician_id: string | null; occurred_at: string; reason: string | null } | null
  /** Paragem comunicada pelo técnico (o telemóvel não para a obra): aberta sem `field_stop_resolved_at`. */
  field_stop_reason?: string | null
  field_stop_at?: string | null
  field_stop_technician_id?: string | null
  field_stop_resolution?: string | null
  field_stop_resolved_at?: string | null
  field_stop_resolved_by?: string | null
  created_at: string
  updated_at: string
  technician_ids: string[]
}

export interface Van {
  id: string
  name: string
  odoo_location: string | null
  technician_id: string | null
  color: string
  trello_label_id: string | null
  active: boolean
  sort_order: number
  /** Equipa do Koortex (`teams.id`) — a carrinha é uma equipa (D2). */
  team_id?: number | string | null
}

export interface Technician {
  id: string
  name: string
  initials: string | null
  is_lead: boolean
  van_id: string | null
  trello_member_id: string | null
  auth_user_id: string | null
  active: boolean
  /** Papel (0.45.0): técnico (carrinha, obras) ou armazém (Ronda no telemóvel). */
  kind?: "tecnico" | "armazem"
  /** Responsável do armazém no telemóvel (só com `kind = armazem`). */
  can_manage_stock?: boolean
}

export interface Absence {
  id: string
  technician_id: string
  date_from: string
  date_to: string
  reason: string | null
}

export interface BoardData {
  jobs: Job[]
  vans: Van[]
  technicians: Technician[]
  absences: Absence[]
  config: { trello_configured: boolean; overload_threshold: number; contracts_backend?: ContractsBackend }
  pending_sync: number
  /** Modo `host`: a lista de contratos não se leu (o quadro avisa). */
  contracts_error?: string
  /** Modo `host`: obras canceladas agora porque o contrato foi cancelado no Koortex (decisão 5). */
  contracts_cancelled?: Array<{ id: string; client_name: string }>
  /** Modo `host`: obras paradas na app retomadas agora porque o contrato foi retomado no Koortex (M6). */
  contracts_resumed?: Array<{ id: string; client_name: string }>
}

export interface Conflict {
  code: string
  severity: "erro" | "aviso"
  message: string
  technician_id?: string
  dates?: string[]
  job_ids?: string[]
}

export interface AssignInput {
  scheduled_date: string | null
  duration_days: number
  van_id: string | null
  lead_technician_id: string | null
  technician_ids: string[]
  note?: string | null
  force?: boolean
  version?: number
}

export type AssignResult =
  | {
      saved: false
      conflicts: Conflict[]
      /** Modo `host`: o contrato recusou a atribuição (nada foi gravado). */
      contract_error?: string
      /** Modo `host` (M4): o contrato ficou com a atribuição e a obra não — «Sincronizar». */
      contract_ahead?: boolean
    }
  | { saved: true; conflicts: Conflict[]; job: Job }

/** Equipa do Koortex (`catalog/teams`) — a carrinha liga-se a uma (D2). */
export interface KoortexTeam {
  id: number
  name: string
  is_active: boolean
  leader_auth_user_id: string | null
  members: number
}

export interface Availability {
  status: "disponivel" | "ocupado" | "ausente"
  reason: string | null
  dates: string[]
  job_ids: string[]
}

export interface AvailabilityResult {
  days: string[]
  availability: Record<string, Availability>
  van_load: Record<string, number>
}

export interface MaterialLine {
  product: string
  default_code: string | null
  demand: number
  reserved: number
  missing: number
  uom: string | null
  picking: string | null
}

export interface MaterialState {
  status: "pronto" | "faltam" | "sem_ordem" | "sem_transferencia" | "erro"
  missing_count: number
  lines: MaterialLine[]
  error?: string
  checked_at: string
}

export interface SaleOrderHit {
  id: number
  name: string | null
  client: { id: number | null; name: string | null; address: string | null; city: string | null }
  amount_total: number | null
  date_order: string | null
  client_order_ref: string | null
}

export interface ProducaoConfig {
  trello: {
    board_id: string | null
    list_planned_id: string | null
    list_in_progress_id: string | null
    list_done_id: string | null
  }
  overload_threshold: number
  contracts_backend: ContractsBackend
  trello_configured: boolean
}


// ─── Contratos do Koortex (modo `host`) ─────────────────────────────────────

/** `GET /contracts-check`: o Koortex está pronto para as obras sobre os contratos? */
export interface ContractsHostCheck {
  ok: boolean
  error: string | null
  options_ok: boolean
  has_type: boolean
  has_payment_terms: boolean
  hold_reasons: number
  vans_without_team: string[]
  warnings: string[]
}

/** Linha do contrato no quadro (de `contracts.list` do host). */
export interface ContractSummary {
  id: string
  /** `por_adjudicar` · `por_planear` · `em_execucao` · `por_auditar` · `em_operacao` · `concluido` · `cancelado`. */
  general_state: string | null
  phase_name: string | null
  current_stage_key: string | null
  status: string
  on_hold_since: string | null
  /** Contrato parado: «Motivo · nota». */
  hold_reason?: string | null
  /** Cancelado no Koortex. */
  cancelled?: boolean
  team: { id: number; name: string | null } | null
  planned_start_at: string | null
  planned_duration_days: number | null
  warning: string | null
}

/**
 * O guia do contrato como o host o devolve (`ContractGuide` do SDK 0.6.0),
 * reduzido ao que os ecrãs mostram. Espelho dos tipos do SDK (o browser não
 * importa o `/server`).
 */
export interface ContractGuideView {
  contract_id: string
  status: string
  general_state: string | null
  current_stage_key: string | null
  current_phase: { name: string; index: number; total: number; entered_at: string | null } | null
  on_hold: { since: string; note: string | null; by_name: string | null; reason_id: number | null; reason_label: string | null; days: number } | null
  guide: {
    phases: Array<{
      stage_key: string
      name: string
      state: "done" | "current" | "future" | "skipped"
      steps: Array<{
        id: string
        key: string
        label: string
        blocking: boolean
        state: "done" | "missing" | "late" | "with_koortex"
        detail?: string | null
        missing?: string[]
        done_by?: { kind: string; name?: string | null; at?: string | null } | null
        items?: Array<{ label: string; checked: boolean; checked_at: string | null; checked_by_name: string | null }>
      }>
    }>
    progress: { done: number; total: number; blocking_missing: string[] }
  }
  header: {
    owner: { id: number; name: string | null } | null
    team: { id: number | null; name: string | null } | null
    planned_start_at: string | null
    planned_duration_days: number | null
    money: { value: number | null }
  }
  payments: { terms_name: string | null; rows: Array<{ milestone_id: string; label: string | null; percent: number | null; amount: number | null; state: string; paid_at: string | null }> }
  planning: { missing: string[] }
  warnings: string[]
}

// ─── App de campo (Fase 3) ──────────────────────────────────────────────────

export interface Device {
  id: string
  technician_id: string
  model: string | null
  os: string | null
  app_version: string | null
  paired_at: string
  last_seen_at: string | null
  revoked_at: string | null
}

export interface PairingCode {
  code: string
  expires_at: string
  qr: string
}

export interface FieldEvent {
  uuid: string
  type: string
  occurred_at: string
  received_at: string
  technician_id: string | null
  data: Record<string, unknown>
  geo: { lat: number; lng: number; accuracy_m?: number } | null
  /** `contrato` = linha do ledger das escritas no contrato do Koortex (modo `host`). */
  status: "aceite" | "recusado" | "contrato"
  reason: string | null
  out_of_assignment: boolean
  /** Ledger do contrato: o host já tinha esta escrita (replay por `event_uuid`). */
  host_replayed?: boolean | null
  process_status: "pendente" | "em_curso" | "feito" | "erro" | "ignorado"
  process_error: string | null
  odoo_ref: string | null
}

export interface FieldMedia {
  id: string
  kind: "foto" | "video" | "assinatura"
  label: string | null
  mime: string | null
  bytes: number | null
  uploaded_at: string
  technician_id: string | null
  url: string | null
}

export interface Closure {
  id: string
  checklist: Record<string, boolean>
  signature_media_id: string | null
  signed_by: string | null
  unsigned_reason: string | null
  instructions_delivered: boolean
  client_email: string | null
  pdf_key: string | null
  pdf_url: string | null
  email_status: "por_enviar" | "a_enviar" | "enviado" | "sem_email" | "erro" | "desligado"
  email_error: string | null
  email_sent_at: string | null
  closed_at: string
}

export interface FieldLog {
  events: FieldEvent[]
  media: FieldMedia[]
  closure: Closure | null
  client_config: { fields: Record<string, string>; secret_keys: string[]; reveals: number } | null
}

export interface VanStockLine {
  odoo_product_id: number
  name: string
  default_code: string | null
  unit: string | null
  tracking: "serial" | "lot" | "none"
  qty: number
  qty_validated: number
  qty_pending: number
  serials: string[]
  serials_pending: string[]
  serials_leaving: string[]
}

export interface FieldConfig {
  odoo_mode: "preparar" | "validar"
  min_app_version: string
  loans_location: string | null
  fsm_enabled: boolean
  fsm_project_id: number | null
  /**
   * `contract_label`: o item da lista «Instalação» do contrato a que este está
   * vinculado (modo `host`, M7). `kinds`: tipos de obra a que se aplica
   * (decisão 6; vazio = todos).
   */
  checklist: Array<{ id: string; label: string; contract_label?: string | null; kinds?: JobKind[] }>
  client_config_fields: Array<{ key: string; label: string; secret: boolean }>
  instructions: { title: string; intro: string; sections: Array<{ title: string; body: string }>; footer: string }
  send_instructions_email: boolean
  video_max_seconds: number
}

type Envelope<T> = { success: true; data: T } | { success: false; error: string }

function unwrap<T>(res: Envelope<T>): T {
  if (!res.success) throw new Error(res.error || "Pedido falhou")
  return res.data
}

const P = "producao"

export const producaoApi = {
  board: async (s: AppShell) => unwrap(await s.client.get<Envelope<BoardData>>(`${P}/board`)),
  job: async (s: AppShell, id: string) => unwrap(await s.client.get<Envelope<Job>>(`${P}/jobs/${id}`)),
  /** A obra de um contrato do Koortex (rota `/contracts/<id>`). */
  jobByContract: async (s: AppShell, contractId: string) =>
    unwrap(await s.client.get<Envelope<{ job_id: string; contract_id: string }>>(`${P}/contracts/${encodeURIComponent(contractId)}`)),
  patchJob: async (
    s: AppShell,
    id: string,
    body: { status?: JobStatus; stop_reason?: string | null; hold_reason_id?: number; note?: string | null; completion_note?: string; version?: number },
  ) => unwrap(await s.client.patch<Envelope<Job>>(`${P}/jobs/${id}`, body)),
  assign: async (s: AppShell, id: string, body: AssignInput) =>
    unwrap(await s.client.post<Envelope<AssignResult>>(`${P}/jobs/${id}/assign`, body)),
  availability: async (
    s: AppShell,
    q: { date: string; duration: number; van_id?: string | null; job_id?: string | null },
  ) => {
    const qs = new URLSearchParams({ date: q.date, duration: String(q.duration) })
    if (q.van_id) qs.set("van_id", q.van_id)
    if (q.job_id) qs.set("job_id", q.job_id)
    return unwrap(await s.client.get<Envelope<AvailabilityResult>>(`${P}/availability?${qs}`))
  },
  material: async (s: AppShell, id: string, fresh = false) =>
    unwrap(await s.client.get<Envelope<MaterialState>>(`${P}/jobs/${id}/material${fresh ? "?fresh=1" : ""}`)),
  syncJob: async (s: AppShell, id: string) => unwrap(await s.client.post<Envelope<Job>>(`${P}/jobs/${id}/sync`, {})),
  syncPending: async (s: AppShell) =>
    unwrap(await s.client.post<Envelope<{ tried: number; synced: number; remaining: number }>>(`${P}/sync-pending`, {})),
  saleOrders: async (s: AppShell, search: string) =>
    unwrap(
      await s.client.get<Envelope<SaleOrderHit[]>>(
        `${P}/sale-orders${search ? `?search=${encodeURIComponent(search)}` : ""}`,
      ),
    ),
  /** Cria a obra; em modo `host` também o contrato — `warning` quando o contrato falhou. */
  fromSaleOrder: async (s: AppShell, so: { id: number; name: string | null }) => {
    const res = await s.client.post<Envelope<Job> & { message?: string }>(`${P}/jobs/from-sale-order`, {
      sale_order_id: so.id,
      ...(so.name ? { sale_order_name: so.name } : {}),
    })
    return { job: unwrap(res), warning: res.success ? res.message ?? null : null }
  },
  /** Modo `host`: reflete no contrato a adjudicação das obras cujo pagamento já está no Odoo (D4). Ninguém adjudica na app. */
  awardsPending: async (s: AppShell) =>
    unwrap(await s.client.post<Envelope<{ checked: number; awarded: number; remaining: number }>>(`${P}/awards-pending`, {})),
  /** Motivos de paragem do tenant (`options().hold_reasons`) — «Suspender» em modo `host`. */
  contractOptions: async (s: AppShell) =>
    unwrap(await s.client.get<Envelope<{ hold_reasons: Array<{ id: number; label: string; is_default: boolean }> }>>(`${P}/contract-options`)),
  /** «Dispensar» a paragem comunicada pelo técnico (fica registado quem). */
  dismissFieldStop: async (s: AppShell, id: string) =>
    unwrap(await s.client.post<Envelope<Job>>(`${P}/jobs/${id}/dismiss-field-stop`, {})),
  /** «Sincronizar» (M4): aplica à obra a equipa, a data e a duração do contrato. */
  syncFromContract: async (s: AppShell, id: string) =>
    unwrap(await s.client.post<Envelope<Job>>(`${P}/jobs/${id}/sync-from-contract`, {})),
  /** «Criar contrato» (modo `host`): a obra sem contrato ganha o seu (D1). */
  createContract: async (s: AppShell, id: string) =>
    unwrap(await s.client.post<Envelope<{ job: Job; contract: ContractGuideView }>>(`${P}/jobs/${id}/contract`, {})),

  vans: async (s: AppShell) => unwrap(await s.client.get<Envelope<Van[]>>(`${P}/vans`)),
  teams: async (s: AppShell) => unwrap(await s.client.get<Envelope<KoortexTeam[]>>(`${P}/teams`)),
  createVan: async (s: AppShell, body: Partial<Van>) => unwrap(await s.client.post<Envelope<Van>>(`${P}/vans`, body)),
  updateVan: async (s: AppShell, id: string, body: Partial<Van>) =>
    unwrap(await s.client.patch<Envelope<Van>>(`${P}/vans/${id}`, body)),
  deleteVan: async (s: AppShell, id: string) => unwrap(await s.client.delete<Envelope<unknown>>(`${P}/vans/${id}`)),

  createTechnician: async (s: AppShell, body: Partial<Technician>) =>
    unwrap(await s.client.post<Envelope<Technician>>(`${P}/technicians`, body)),
  updateTechnician: async (s: AppShell, id: string, body: Partial<Technician>) =>
    unwrap(await s.client.patch<Envelope<Technician>>(`${P}/technicians/${id}`, body)),
  deleteTechnician: async (s: AppShell, id: string) =>
    unwrap(await s.client.delete<Envelope<unknown>>(`${P}/technicians/${id}`)),

  createAbsence: async (s: AppShell, body: Omit<Absence, "id">) =>
    unwrap(await s.client.post<Envelope<Absence>>(`${P}/absences`, body)),
  deleteAbsence: async (s: AppShell, id: string) =>
    unwrap(await s.client.delete<Envelope<unknown>>(`${P}/absences/${id}`)),

  config: async (s: AppShell) => unwrap(await s.client.get<Envelope<ProducaoConfig>>(`${P}/config`)),
  /** Verificação do Koortex antes de passar as obras para os contratos. */
  contractsCheck: async (s: AppShell) => unwrap(await s.client.get<Envelope<ContractsHostCheck>>(`${P}/contracts-check`)),
  saveConfig: async (
    s: AppShell,
    body: { trello?: Partial<ProducaoConfig["trello"]>; overload_threshold?: number; contracts_backend?: ContractsBackend },
  ) => unwrap(await s.client.patch<Envelope<ProducaoConfig & { warnings?: string[] }>>(`${P}/config`, body)),
  devices: async (s: AppShell) => unwrap(await s.client.get<Envelope<Device[]>>(`${P}/devices`)),
  pairTechnician: async (s: AppShell, id: string) =>
    unwrap(await s.client.post<Envelope<PairingCode>>(`${P}/technicians/${id}/pair`, {})),
  revokeDevice: async (s: AppShell, id: string) =>
    unwrap(await s.client.post<Envelope<Device>>(`${P}/devices/${id}/revoke`, {})),
  fieldLog: async (s: AppShell, jobId: string) =>
    unwrap(await s.client.get<Envelope<FieldLog>>(`${P}/jobs/${jobId}/field-log`)),
  revealClientConfig: async (s: AppShell, jobId: string) =>
    unwrap(await s.client.post<Envelope<{ fields: Record<string, string> }>>(`${P}/jobs/${jobId}/client-config`, {})),
  resendClosure: async (s: AppShell, jobId: string, body: { email?: string; regenerate?: boolean }) =>
    unwrap(await s.client.post<Envelope<Closure>>(`${P}/jobs/${jobId}/closure-resend`, body)),
  applyFieldEvent: async (s: AppShell, jobId: string, uuid: string) =>
    unwrap(await s.client.post<Envelope<{ applied: boolean; process_status?: string; process_error?: string | null }>>(`${P}/jobs/${jobId}/field-apply`, { uuid })),
  vanStock: async (s: AppShell, vanId: string) =>
    unwrap(await s.client.get<Envelope<{ van_id: string; location: string; lines: VanStockLine[] }>>(`${P}/vans/${vanId}/stock`)),
  vanFieldEvents: async (s: AppShell) =>
    unwrap(await s.client.get<Envelope<{ events: Array<FieldEvent & { van_id: string | null }> }>>(`${P}/field-events`)),
  applyVanFieldEvent: async (s: AppShell, uuid: string) =>
    unwrap(await s.client.post<Envelope<{ applied: boolean; process_status?: string; process_error?: string | null }>>(`${P}/field-events/apply`, { uuid })),
  retryVanFieldEvents: async (s: AppShell) =>
    unwrap(await s.client.post<Envelope<{ retried: number }>>(`${P}/field-events/retry`, {})),
  retryField: async (s: AppShell, jobId: string) =>
    unwrap(await s.client.post<Envelope<{ retried: number }>>(`${P}/jobs/${jobId}/field-retry`, {})),
  fieldConfig: async (s: AppShell) => unwrap(await s.client.get<Envelope<FieldConfig>>(`${P}/field-config`)),
  /** Em modo `host` devolve também `warnings`: itens da checklist sem correspondência no contrato (M7). */
  saveFieldConfig: async (s: AppShell, body: Partial<FieldConfig>) =>
    unwrap(await s.client.patch<Envelope<FieldConfig & { warnings?: string[] }>>(`${P}/field-config`, body)),
  trello: async (s: AppShell, what: "lists" | "labels" | "members", boardId: string) =>
    unwrap(
      await s.client.get<Envelope<Array<Record<string, string | boolean | null>>>>(
        `${P}/trello?what=${what}&board_id=${encodeURIComponent(boardId)}`,
      ),
    ),
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}
